import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, AlertCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

const signupSchema = z.object({
  email: z.string().email("Invalid email address"),
  display_name: z.string().min(2, "Name must be at least 2 characters"),
  title: z.string().optional(),
  organization: z.string().optional(),
  bio: z.string().optional(),
  linkedin_url: z.string().url("Invalid LinkedIn URL").optional().or(z.literal("")),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirm_password: z.string(),
  data_consent: z.boolean().refine((val) => val === true, {
    message: "You must accept the data collection policy",
  }),
}).refine((data) => data.password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

const SignupFromInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [selectedNetworkMembers, setSelectedNetworkMembers] = useState<string[]>([]);

  // Fetch all profiles for network member selection
  const { data: allProfiles } = useQuery({
    queryKey: ['all-profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, email, avatar_url')
        .order('display_name');

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    const token = searchParams.get("token");
    const storedData = sessionStorage.getItem("invitation_data");

    if (!token || !storedData) {
      navigate("/auth");
      return;
    }

    const data = JSON.parse(storedData);
    setInvitationData(data);

    // Auto-include referrer in network
    if (data.inviter_id) {
      setSelectedNetworkMembers([data.inviter_id]);
    }
  }, [searchParams, navigate]);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: invitationData?.invitee_email || "",
      display_name: invitationData?.invitee_name || "",
      title: "",
      organization: "",
      bio: "",
      linkedin_url: "",
      password: "",
      confirm_password: "",
      data_consent: false,
    },
  });

  useEffect(() => {
    if (invitationData) {
      form.setValue("email", invitationData.invitee_email);
      if (invitationData.invitee_name) {
        form.setValue("display_name", invitationData.invitee_name);
      }
    }
  }, [invitationData, form]);

  const toggleNetworkMember = (memberId: string) => {
    // Don't allow removing the referrer
    if (memberId === invitationData?.inviter_id) return;

    setSelectedNetworkMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const onSubmit = async (values: SignupFormValues) => {
    setIsLoading(true);

    try {
      // Create Supabase auth account
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            display_name: values.display_name,
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("Failed to create account");

      // Insert profile with all data
      const { error: profileError } = await supabase.from("profiles").insert({
        id: authData.user.id,
        email: values.email,
        display_name: values.display_name,
        bio: values.bio || null,
        organization: values.organization || null,
        phone: null,
        avatar_url: null,
        data_collection_consent: values.data_consent,
        data_collection_consent_at: new Date().toISOString(),
      });

      if (profileError) throw profileError;

      // Update invitation to registered status
      const { error: invitationUpdateError } = await supabase
        .from("invitations")
        .update({
          invitation_status: "registered",
          registered_at: new Date().toISOString(),
          registered_user_id: authData.user.id,
        })
        .eq("tracking_token", searchParams.get("token"));

      if (invitationUpdateError) throw invitationUpdateError;

      // Create referral metrics entry
      const { error: metricsError } = await supabase
        .from("referral_metrics")
        .insert({
          referral_user_id: authData.user.id,
          inviter_id: invitationData.inviter_id,
          invitation_id: invitationData.id,
          channel_id: invitationData.channel_id,
          onboarding_started_at: new Date().toISOString(),
        });

      if (metricsError) throw metricsError;

      // Track account creation activity
      await supabase.functions.invoke("track-user-activity", {
        body: {
          action_type: "account_created",
          action_target: "/signup-from-invitation",
          action_metadata: {
            channel: invitationData.channel_name,
            invited_by: invitationData.inviter_id,
          },
        },
      });

      toast({
        title: "Account created successfully!",
        description: "Welcome to the iSPAI Knowledge Network",
      });

      // Clear session storage
      sessionStorage.removeItem("invitation_data");

      // Redirect to profile creation or dashboard
      navigate("/create-myspai-profile");
    } catch (error: any) {
      console.error("Signup error:", error);
      toast({
        title: "Signup failed",
        description: error.message || "An error occurred during signup",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!invitationData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Create Your iSPAI Account</CardTitle>
            <CardDescription>
              You've been invited to join the {invitationData.channel_name}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-6 border-primary/50 bg-primary/5">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong className="block mb-2">MVP Data Collection Notice</strong>
                During our MVP phase, iSPAI tracks user engagement metrics to test and improve user
                experience. This includes onboarding pace, activity patterns, feature usage, and
                content creation.
                <br />
                <br />
                <strong>Important:</strong> Please avoid uploading any content that might be
                considered private intellectual property or confidential information.
                <br />
                <br />
                By creating an account, you acknowledge and consent to this data collection policy.
              </AlertDescription>
            </Alert>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  {...form.register("email")}
                  readOnly
                  className="bg-muted"
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name *</Label>
                <Input id="display_name" {...form.register("display_name")} />
                {form.formState.errors.display_name && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.display_name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input id="title" {...form.register("title")} placeholder="e.g., Innovation Director" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization">Organization</Label>
                <Input id="organization" {...form.register("organization")} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea id="bio" {...form.register("bio")} rows={4} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedin_url">LinkedIn URL</Label>
                <Input
                  id="linkedin_url"
                  {...form.register("linkedin_url")}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
                {form.formState.errors.linkedin_url && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.linkedin_url.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Network Members</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Your referrer is automatically included. Select additional members to add to your network:
                </p>
                <div className="border rounded-md p-4 max-h-60 overflow-y-auto space-y-2">
                  {allProfiles?.map((profile) => (
                    <div key={profile.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`member-${profile.id}`}
                        checked={selectedNetworkMembers.includes(profile.id)}
                        onCheckedChange={() => toggleNetworkMember(profile.id)}
                        disabled={profile.id === invitationData?.inviter_id}
                      />
                      <Label
                        htmlFor={`member-${profile.id}`}
                        className="flex-1 cursor-pointer text-sm"
                      >
                        {profile.display_name} {profile.id === invitationData?.inviter_id && "(Referrer)"}
                        <span className="text-muted-foreground ml-2 text-xs">
                          {profile.email}
                        </span>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input id="password" type="password" {...form.register("password")} />
                  {form.formState.errors.password && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.password.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm Password *</Label>
                  <Input id="confirm_password" type="password" {...form.register("confirm_password")} />
                  {form.formState.errors.confirm_password && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.confirm_password.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <Controller
                  name="data_consent"
                  control={form.control}
                  render={({ field }) => (
                    <Checkbox 
                      id="data_consent" 
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <label htmlFor="data_consent" className="text-sm leading-relaxed">
                  I acknowledge the MVP Data Collection Notice and consent to engagement tracking. I
                  understand that I should not upload private IP or confidential content during this
                  testing period.
                </label>
              </div>
              {form.formState.errors.data_consent && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.data_consent.message}
                </p>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Account
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignupFromInvitation;
